import React, { useState, useEffect } from 'react';
import { Plus, Share2, Users, Bell } from 'lucide-react';
import { format, addWeeks } from 'date-fns';
import { fr } from 'date-fns/locale';
import { collection, query, where, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import CalendarView from '../components/CalendarView';
import FollowUpScheduler from '../components/FollowUpScheduler';
import AlertsPanel, { Alert } from '../components/AlertsPanel';
import AppointmentForm from '../components/AppointmentForm';
import CalendarSharing from '../components/CalendarSharing';
import CalendarSwitcher from '../components/CalendarSwitcher';
import { useThemeStore } from '../stores/themeStore';
import { Appointment } from '../components/CalendarView';
import { toast } from 'react-hot-toast';
import { useAgenciesStore } from '../stores/agenciesStore';
import { emailService } from '../services/emailService';
import { notificationService } from '../services/notificationService';
import { calculateAlertDate, createAppointmentAlert } from '../utils/appointmentUtils';

function RendezVous() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [preselectedTime, setPreselectedTime] = useState<string | undefined>();
  const [selectedUserId, setSelectedUserId] = useState<string>(auth.currentUser?.uid || '');
  const [isLoading, setIsLoading] = useState(false);
  const { darkMode } = useThemeStore();
  const { loadAgencies } = useAgenciesStore();

  useEffect(() => {
    if (auth.currentUser) {
      setSelectedUserId(auth.currentUser.uid);
      loadAppointments(auth.currentUser.uid);
      loadAlerts(auth.currentUser.uid);
      loadAgencies();
      
      // Vérifier les alertes à afficher
      notificationService.checkAppointmentAlerts();
    }
  }, []);

  const loadAppointments = async (userId: string) => {
    setIsLoading(true);
    try {
      const appointmentsQuery = query(
        collection(db, 'appointments'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(appointmentsQuery);
      const appointmentsData: Appointment[] = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        appointmentsData.push({
          id: doc.id,
          title: data.title,
          date: data.date && typeof data.date.toDate === 'function' ? data.date.toDate() : new Date(data.date),
          time: data.time,
          agency: data.agency,
          contact: data.contact,
          priority: data.priority,
          userId: data.userId, // Explicitly include userId
          alertDate: data.alertDate && typeof data.alertDate.toDate === 'function' ? data.alertDate.toDate() : (data.alertDate ? new Date(data.alertDate) : undefined),
          alertType: data.alertType,
          alertMinutes: data.alertMinutes,
          suivi: data.suivi
        });
      });
      
      setAppointments(appointmentsData);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Erreur lors du chargement des rendez-vous');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAlerts = async (userId: string) => {
    try {
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(alertsQuery);
      const alertsData: Alert[] = [];
      
      querySnapshot.forEach(doc => {
        const data = doc.data();
        alertsData.push({
          id: doc.id,
          type: data.type,
          company: data.company,
          date: data.date && typeof data.date.toDate === 'function' ? data.date.toDate() : new Date(data.date),
          agency: data.agency,
          status: data.status,
          step: data.step,
          description: data.description,
          action: data.action
        });
      });
      
      setAlerts(alertsData);
    } catch (error) {
      console.error('Error loading alerts:', error);
      toast.error('Erreur lors du chargement des alertes');
    }
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentDate(date); // Mettre à jour la date courante pour la navigation
  };

  const handleFollowUp = (appointment: Appointment) => {
    setSelectedCompany(appointment.title);
    setShowFollowUp(true);
  };

  const handleScheduleAction = async (step: any) => {
    if (!selectedCompany || !selectedDate || !auth.currentUser) return;

    try {
      const newAlert: Alert = {
        id: Date.now().toString(),
        type: 'relance',
        company: selectedCompany,
        date: addWeeks(selectedDate, step.id),
        agency: 'Marseille 4',
        status: 'pending',
        step: step.id,
        description: step.description,
        action: step.type === 'call' ? 'Appel à effectuer' : 
                step.type === 'email' ? 'Email à envoyer' : 
                step.type === 'phone_follow_up' ? 'Relance téléphonique' :
                step.type === 'email_follow_up' ? 'Relance par email' :
                'Newsletter à préparer'
      };

      // Sauvegarder l'alerte dans Firestore
      await addDoc(collection(db, 'alerts'), {
        ...newAlert,
        date: newAlert.date,
        userId: auth.currentUser.uid,
        appointmentId: Date.now().toString(), // ID temporaire
        emailSent: false,
        displayed: false,
        createdAt: serverTimestamp()
      });

      // Créer un rendez-vous pour cette action de suivi
      const appointmentData = {
        title: `Suivi: ${selectedCompany}`,
        date: newAlert.date,
        time: '09:00', // Heure par défaut
        agency: newAlert.agency,
        contact: 'Contact entreprise',
        priority: 'medium' as const,
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        status: 'pending',
        suivi: step.description
      };

      const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
      
      // Ajouter le rendez-vous à l'état local
      const newAppointment: Appointment = {
        id: docRef.id,
        title: appointmentData.title,
        date: newAlert.date,
        time: appointmentData.time,
        agency: appointmentData.agency,
        contact: appointmentData.contact,
        priority: appointmentData.priority,
        userId: auth.currentUser.uid,
        suivi: step.description
      };
      
      setAppointments(prev => [...prev, newAppointment]);
      setAlerts(prev => [...prev, newAlert]);
      setShowFollowUp(false);
      toast.success('Action de suivi planifiée');
      
      // Recharger les rendez-vous et les alertes
      loadAppointments(auth.currentUser.uid);
      loadAlerts(auth.currentUser.uid);
    } catch (error) {
      console.error('Error scheduling follow-up:', error);
      toast.error('Erreur lors de la planification du suivi');
    }
  };

  const handleUpdateAlertStatus = async (alertId: string, status: Alert['status']) => {
    try {
      // Mettre à jour l'alerte dans Firestore
      const alertRef = doc(db, 'alerts', alertId);
      await updateDoc(alertRef, { status });

      setAlerts(alerts.map(alert => 
        alert.id === alertId ? { ...alert, status } : alert
      ));
      
      toast.success('Statut de l\'alerte mis à jour');
      
      // Si l'alerte est marquée comme en retard, envoyer une notification par email
      if (status === 'late' && auth.currentUser?.email) {
        await emailService.sendLateAppointmentsNotification(auth.currentUser.email);
      }
    } catch (error) {
      console.error('Error updating alert status:', error);
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteAlert = async (alertId: string) => {
    try {
      // Supprimer l'alerte de Firestore
      const alertRef = doc(db, 'alerts', alertId);
      await deleteDoc(alertRef);

      // Mettre à jour l'état local
      setAlerts(alerts.filter(alert => alert.id !== alertId));
      
      toast.success('Alerte supprimée');
    } catch (error) {
      console.error('Error deleting alert:', error);
      toast.error('Erreur lors de la suppression de l\'alerte');
    }
  };

  const handleAddAppointment = async (data: any) => {
    const user = auth.currentUser;
    if (!user) {
      toast.error('Vous devez être connecté pour ajouter un rendez-vous');
      return;
    }

    try {
      // Rafraîchir le token d'authentification pour éviter les erreurs de permissions
      await user.getIdToken(true);
      
      const appointmentData = {
        title: data.title,
        date: new Date(data.date),
        time: data.time,
        agency: data.agency,
        contact: data.contact,
        priority: data.priority,
        userId: user.uid,
        createdAt: serverTimestamp(),
        suivi: data.suivi
      };

      // Ajouter les données d'alerte si activées
      if (data.enableAlert) {
        appointmentData.alertDate = data.alertDate;
        appointmentData.alertType = data.alertType;
        appointmentData.alertMinutes = parseInt(data.alertTime);
      }

      if (editMode && selectedAppointment) {
        // Mise à jour d'un rendez-vous existant
        const appointmentRef = doc(db, 'appointments', selectedAppointment.id);
        await updateDoc(appointmentRef, appointmentData);
        
        setAppointments(prev => prev.map(apt => 
          apt.id === selectedAppointment.id ? { ...apt, ...appointmentData, userId: selectedAppointment.userId } : apt
        ));
        
        toast.success('Rendez-vous mis à jour');
      } else {
        // Ajout d'un nouveau rendez-vous
        const docRef = await addDoc(collection(db, 'appointments'), appointmentData);
        
        const newAppointment: Appointment = {
          id: docRef.id,
          title: data.title,
          date: new Date(data.date),
          time: data.time,
          agency: data.agency,
          contact: data.contact,
          priority: data.priority,
          userId: auth.currentUser.uid, // Ensure userId is included
          action: `Rappel de rendez-vous (notification)`
        };

        if (data.enableAlert) {
          newAppointment.alertDate = data.alertDate;
          newAppointment.alertType = data.alertType;
          newAppointment.alertMinutes = parseInt(data.alertTime);
          
          // Créer une alerte pour le rendez-vous
          const newAlert: Alert = {
            id: `apt-${docRef.id}`,
            type: 'rendez-vous',
            company: data.title,
            date: data.alertDate,
            agency: data.agency,
            status: 'pending',
            step: 0,
            description: `Rendez-vous avec ${data.contact} à ${data.time}`,
            action: `Rappel de rendez-vous (${data.alertType})`
          };
          
          await addDoc(collection(db, 'alerts'), {
            ...newAlert,
            date: newAlert.date,
            userId: auth.currentUser.uid,
            appointmentId: docRef.id,
            emailSent: false,
            displayed: false,
            createdAt: serverTimestamp()
          });
          
          setAlerts(prev => [...prev, newAlert]);
        }
        
        setAppointments(prev => [...prev, newAppointment]);
        toast.success('Rendez-vous créé');
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast.error('Erreur lors de l\'enregistrement du rendez-vous');
    } finally {
      setSelectedAppointment(null);
      setShowNewAppointment(false);
      setEditMode(false);
      setPreselectedTime(undefined);
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setEditMode(true);
    setShowNewAppointment(true);
    setPreselectedTime(undefined);
  };

  const handleTimeSlotClick = (date: Date, hour?: number) => {
    setSelectedDate(date);
    
    // Formater l'heure au format HH:00
    let formattedTime;
    if (hour !== undefined) {
      formattedTime = `${hour.toString().padStart(2, '0')}:00`;
      setPreselectedTime(formattedTime);
    } else {
      setPreselectedTime(undefined);
    }
    
    setShowNewAppointment(true);
    setEditMode(false);
    setSelectedAppointment(null);
  };

  const handleDeleteAppointment = async () => {
    if (!selectedAppointment || !auth.currentUser) return;
    
    try {
      // Supprimer le rendez-vous de Firestore
      await deleteDoc(doc(db, 'appointments', selectedAppointment.id));
      
      // Supprimer les alertes associées
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('appointmentId', '==', selectedAppointment.id)
      );
      
      const alertsSnapshot = await getDocs(alertsQuery);
      alertsSnapshot.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });
      
      // Note: emailQueue deletion is handled by server-side Cloud Function
      // The client should not attempt to delete from emailQueue collection
      
      // Mettre à jour l'état local
      setAppointments(prev => prev.filter(apt => apt.id !== selectedAppointment.id));
      setAlerts(prev => prev.filter(alert => 
        !(alert.type === 'rendez-vous' && alert.id.includes(selectedAppointment.id))
      ));
      
      setSelectedAppointment(null);
      setShowNewAppointment(false);
      setEditMode(false);
      
      toast.success('Rendez-vous supprimé');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Erreur lors de la suppression du rendez-vous');
    }
  };

  const handleUserChange = (userId: string) => {
    setSelectedUserId(userId);
    loadAppointments(userId);
    loadAlerts(userId);
  };

  const calendarAlerts = alerts.map(alert => ({
    date: alert.date,
    status: alert.status
  }));

  const isOwnCalendar = selectedUserId === auth.currentUser?.uid;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rendez-vous</h1>
        <div className="flex items-center gap-4">
          <CalendarSwitcher 
            selectedUserId={selectedUserId}
            onSelectUser={handleUserChange}
          />
          
          {isOwnCalendar && (
            <>
              <button 
                onClick={() => setShowSharingModal(true)}
                className={`flex items-center px-4 py-2 ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-50 text-blue-600'} rounded-2xl hover:${darkMode ? 'bg-blue-800' : 'bg-blue-100'} transition-colors`}
              >
                <Users className="w-5 h-5 mr-2" />
                Partager
              </button>
              
              <button 
                onClick={() => {
                  setShowNewAppointment(true);
                  setEditMode(false);
                  setSelectedAppointment(null);
                  setPreselectedTime(undefined);
                }}
                className="flex items-center px-4 py-2 bg-gradient-anthea text-white rounded-2xl hover:opacity-90 transition-opacity"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nouveau rendez-vous
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-sm`}>
            <div className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anthea-blue"></div>
                </div>
              ) : (
                <CalendarView
                  currentDate={currentDate}
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  alerts={calendarAlerts}
                  appointments={appointments}
                  onAppointmentClick={isOwnCalendar ? handleAppointmentClick : undefined}
                  onTimeSlotClick={isOwnCalendar ? handleTimeSlotClick : undefined}
                />
              )}

              {selectedDate && (
                <div className="mt-8">
                  <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Rendez-vous du {format(selectedDate, 'd MMMM yyyy', { locale: fr })}
                  </h3>
                  <div className="space-y-4">
                    {appointments
                      .filter(apt => {
                        const aptDate = new Date(apt.date);
                        return (
                          aptDate.getDate() === selectedDate.getDate() &&
                          aptDate.getMonth() === selectedDate.getMonth() &&
                          aptDate.getFullYear() === selectedDate.getFullYear()
                        );
                      })
                      .map((apt) => (
                        <div 
                          key={apt.id} 
                          className={`flex items-center justify-between p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl cursor-pointer hover:shadow-md transition-shadow`}
                          onClick={() => isOwnCalendar && handleAppointmentClick(apt)}
                        >
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{apt.title}</p>
                            <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{apt.time}</p>
                            {apt.suivi && (
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>Suivi: {apt.suivi}</p>
                            )}
                            {apt.alertDate && (
                              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center mt-1`}>
                                <Bell className="w-3 h-3 mr-1" />
                                Rappel {apt.alertMinutes} min avant ({apt.alertType === 'email' ? 'email' : apt.alertType === 'notification' ? 'notification' : 'email et notification'})
                              </p>
                            )}
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{apt.agency}</p>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>{apt.contact}</p>
                            </div>
                            {isOwnCalendar && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFollowUp(apt);
                                }}
                                className="px-3 py-1.5 text-sm bg-gradient-anthea text-white rounded-xl hover:opacity-90 transition-opacity"
                              >
                                Planifier le suivi
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    {appointments.filter(apt => {
                      const aptDate = new Date(apt.date);
                      return (
                        aptDate.getDate() === selectedDate.getDate() &&
                        aptDate.getMonth() === selectedDate.getMonth() &&
                        aptDate.getFullYear() === selectedDate.getFullYear()
                      );
                    }).length === 0 && (
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Aucun rendez-vous prévu pour cette date.
                        {isOwnCalendar && " Cliquez sur un créneau horaire pour en ajouter un."}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {showFollowUp && selectedCompany && (
            <div className="mt-8">
              <FollowUpScheduler
                initialContactDate={selectedDate || new Date()}
                onScheduleAction={handleScheduleAction}
              />
            </div>
          )}
        </div>

        <div className="lg:col-span-1">
          <AlertsPanel
            alerts={alerts}
            onUpdateStatus={isOwnCalendar ? handleUpdateAlertStatus : undefined}
            onDeleteAlert={isOwnCalendar ? handleDeleteAlert : undefined}
          />
        </div>
      </div>

      {showNewAppointment && (
        <AppointmentForm
          onSubmit={handleAddAppointment}
          onClose={() => {
            setShowNewAppointment(false);
            setSelectedAppointment(null);
            setEditMode(false);
            setPreselectedTime(undefined);
          }}
          selectedDate={selectedDate}
          appointment={editMode ? selectedAppointment : undefined}
          isEditing={editMode}
          preselectedTime={preselectedTime}
          onDelete={editMode ? handleDeleteAppointment : undefined}
        />
      )}

      {showSharingModal && (
        <CalendarSharing onClose={() => setShowSharingModal(false)} />
      )}
    </div>
  );
}

export default RendezVous;